import React, { useState, useMemo } from 'react';
import { KeywordWithIntent, Product, ViewType } from '../types';
import { checkCopyAvailability, groupProductsByBrand, groupProductsBySubBrand } from '../utils/keywordCopy';

interface CopyKeywordPanelProps {
  isOpen: boolean;
  keyword: KeywordWithIntent | null;
  sourceProductId: string | null;
  sourceGroupName: string | null;
  allProducts: Product[];
  currentViewType: ViewType;
  onClose: () => void;
  onCopy: (keyword: KeywordWithIntent, targetProductIds: string[]) => void;
}

interface GroupNode {
  id: string;
  name: string;
  type: 'group' | 'product';
  products?: Product[];
  available: boolean;
  reason?: string;
  children?: GroupNode[];
}

const CopyKeywordPanel: React.FC<CopyKeywordPanelProps> = ({
  isOpen,
  keyword,
  sourceProductId,
  sourceGroupName,
  allProducts,
  currentViewType,
  onClose,
  onCopy,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Build tree structure based on current view type
  const treeNodes = useMemo(() => {
    if (!keyword) return [];

    const nodes: GroupNode[] = [];

    if (currentViewType === 'product') {
      // Simple flat list of products
      allProducts.forEach(product => {
        const availability = checkCopyAvailability(keyword, [product], sourceProductId || undefined);
        const isAvailable = availability[0]?.available || false;
        
        nodes.push({
          id: product.id,
          name: product.name,
          type: 'product',
          available: isAvailable,
          reason: availability[0]?.reason
        });
      });
    } else if (currentViewType === 'brand') {
      // Group by brands, with expandable products
      const brandGroups = groupProductsByBrand(allProducts);
      
      brandGroups.forEach((products, brandName) => {
        const availability = checkCopyAvailability(keyword, products, sourceProductId || undefined);
        const availableProducts = availability.filter(a => a.available);
        const hasAnyAvailable = availableProducts.length > 0;
        
        const children: GroupNode[] = products.map(product => {
          const productAvailability = availability.find(a => a.productId === product.id);
          return {
            id: product.id,
            name: product.name,
            type: 'product',
            available: productAvailability?.available || false,
            reason: productAvailability?.reason
          };
        });

        nodes.push({
          id: brandName,
          name: brandName,
          type: 'group',
          products: products,
          available: hasAnyAvailable,
          reason: !hasAnyAvailable ? 'No available products' : undefined,
          children
        });
      });
    } else if (currentViewType === 'sub-brand') {
      // Group by sub-brands, with expandable products
      const subBrandGroups = groupProductsBySubBrand(allProducts);
      
      subBrandGroups.forEach((products, subBrandName) => {
        const availability = checkCopyAvailability(keyword, products, sourceProductId || undefined);
        const availableProducts = availability.filter(a => a.available);
        const hasAnyAvailable = availableProducts.length > 0;
        
        const children: GroupNode[] = products.map(product => {
          const productAvailability = availability.find(a => a.productId === product.id);
          return {
            id: product.id,
            name: product.name,
            type: 'product',
            available: productAvailability?.available || false,
            reason: productAvailability?.reason
          };
        });

        nodes.push({
          id: subBrandName,
          name: subBrandName,
          type: 'group',
          products: products,
          available: hasAnyAvailable,
          reason: !hasAnyAvailable ? 'No available products' : undefined,
          children
        });
      });
    }

    return nodes;
  }, [keyword, allProducts, currentViewType, sourceProductId]);

  // Filter tree nodes based on search query
  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) return treeNodes;

    const query = searchQuery.toLowerCase();
    return treeNodes.filter(node => {
      if (node.type === 'product') {
        return node.name.toLowerCase().includes(query);
      } else {
        // For groups, include if group name matches OR any child matches
        const groupMatches = node.name.toLowerCase().includes(query);
        const childMatches = node.children?.some(child => 
          child.name.toLowerCase().includes(query)
        );
        return groupMatches || childMatches;
      }
    });
  }, [treeNodes, searchQuery]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleProduct = (productId: string, available: boolean) => {
    if (!available) return;

    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const selectAllInGroup = (node: GroupNode) => {
    if (!node.children) return;

    const availableProductIds = node.children
      .filter(child => child.available)
      .map(child => child.id);

    setSelectedProductIds(prev => {
      const next = new Set(prev);
      availableProductIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleConfirm = () => {
    if (keyword && selectedProductIds.size > 0) {
      onCopy(keyword, Array.from(selectedProductIds));
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedProductIds(new Set());
    setExpandedGroups(new Set());
    onClose();
  };

  if (!isOpen || !keyword) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Slide-out Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Copy Keyword</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="text-sm text-gray-600 mb-3">
            <span className="font-medium text-gray-900">"{keyword.text}"</span>
            <div className="text-xs mt-1">
              From: {sourceGroupName || 'Unknown'}
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 text-sm"
          />
        </div>

        {/* Tree View */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredNodes.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {searchQuery ? 'No matching targets found' : 'No targets available'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredNodes.map(node => (
                <div key={node.id}>
                  {node.type === 'product' ? (
                    // Product Node
                    <button
                      onClick={() => toggleProduct(node.id, node.available)}
                      disabled={!node.available}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        node.available
                          ? selectedProductIds.has(node.id)
                            ? 'bg-accent-100 border-2 border-accent-500 text-accent-900'
                            : 'hover:bg-gray-100 border-2 border-transparent'
                          : 'opacity-50 cursor-not-allowed bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedProductIds.has(node.id)}
                          disabled={!node.available}
                          onChange={() => {}}
                          className="w-4 h-4 text-accent-600 rounded focus:ring-accent-500"
                        />
                        <span className="font-medium">{node.name}</span>
                      </div>
                      {!node.available && node.reason && (
                        <span className="text-xs text-gray-500">({node.reason})</span>
                      )}
                    </button>
                  ) : (
                    // Group Node
                    <div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleGroup(node.id)}
                          className="flex-1 flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                expandedGroups.has(node.id) ? 'transform rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span>{node.name}</span>
                            <span className="text-xs text-gray-500">
                              ({node.children?.length || 0} products)
                            </span>
                          </div>
                          {!node.available && (
                            <span className="text-xs text-red-600">(all exist)</span>
                          )}
                        </button>
                        {node.available && (
                          <button
                            onClick={() => selectAllInGroup(node)}
                            className="px-2 py-1 text-xs text-accent-600 hover:text-accent-800 font-medium"
                          >
                            Select All
                          </button>
                        )}
                      </div>

                      {/* Children */}
                      {expandedGroups.has(node.id) && node.children && (
                        <div className="ml-6 mt-1 space-y-1">
                          {node.children.map(child => (
                            <button
                              key={child.id}
                              onClick={() => toggleProduct(child.id, child.available)}
                              disabled={!child.available}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                                child.available
                                  ? selectedProductIds.has(child.id)
                                    ? 'bg-accent-100 border-2 border-accent-500 text-accent-900'
                                    : 'hover:bg-gray-100 border-2 border-transparent'
                                  : 'opacity-50 cursor-not-allowed bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedProductIds.has(child.id)}
                                  disabled={!child.available}
                                  onChange={() => {}}
                                  className="w-4 h-4 text-accent-600 rounded focus:ring-accent-500"
                                />
                                <span>{child.name}</span>
                              </div>
                              {!child.available && child.reason && (
                                <span className="text-xs text-gray-500">({child.reason})</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">
              {selectedProductIds.size} {currentViewType === 'product' ? 'product' : 'products'} selected
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedProductIds.size === 0}
              className="flex-1 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Copy to {selectedProductIds.size} {selectedProductIds.size === 1 ? currentViewType : `${currentViewType}s`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CopyKeywordPanel;
